import {
  EmployeeWorkDays,
  TeamAvailability,
  Holiday,
  GlobalHolidayYear,
  Supplier,
  PlanningAbsence,
  Note,
  DailyRemark,
} from "./dto";
import {Task, TaskStatus} from "../../../lib/src/api/dto/Task";
import {Team} from "../../../lib/src/api/dto/Team";
import {QuantityTypeTemplate} from "../../../lib/src/api/dto/QuantityTypeTemplate";
import {Employee, EmployeeType} from "../../../lib/src/api/dto/Employee"
import {QuantityType} from "../../../lib/src/api/dto/QuantityType"
import {useCallback} from "react";
import {getToken} from "@ventumsoftware/react-toolkit";
import moment from "moment";
import config from "../config";
import {filterUndefinedValues} from "@traas/lib/src/util/filterUndefinedValues";
import {Order} from "../../../lib/src/api/dto/Order";
import {Relation} from "../../../lib/src/api/dto/Relation";
import {VatRate} from "@traas/lib/src/api/dto/VatRate";
import {Ledger} from "@traas/lib/src/api/dto/Ledger";
import {Workorder} from "../../../lib/src/api/dto/Workorder";

const API_BASE_URL = config.apiBaseUrl

export const useApiCall = () => {
  const checkForValidationErrors = async (response: Response): Promise<void> => {
    if (response.status === 422) {
      const json = await response.json()
      if ("message" in json && "errors" in json && typeof json.errors === "object") {
        throw new ValidationError(json.message, json.errors)
      }
      if ("message" in json) {
        throw new ValidationError(json.message, {'message': [json.message]})
      }
    }
  }

  const get = useCallback(async function<T>(url: string) {
    const response = await fetch(`${API_BASE_URL}/${url}`, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${getToken()}`
      }
    })
    await checkForValidationErrors(response)
    return await response.json() as {data: T}
  }, [])
  const put = useCallback(async function<T>(url: string, body: object) {
    const response = await fetch(`${API_BASE_URL}/${url}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${getToken()}`
      },
      body: JSON.stringify(body)
    })
    await checkForValidationErrors(response)
    return await response.json() as {data: T}
  }, [])
  const post = useCallback(async function<T>(url: string, body: object) {
    const response = await fetch(`${API_BASE_URL}/${url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${getToken()}`
      },
      body: JSON.stringify(body)
    })
    await checkForValidationErrors(response)
    return await response.json() as {data: T}
  }, [])
  const postRaw = useCallback(async function<T>(url: string, body: object) {
    const response = await fetch(`${API_BASE_URL}/${url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`
      },
      body: JSON.stringify(body)
    })
    await checkForValidationErrors(response)
    return response
  }, [])
  const postForm = useCallback(async function<T>(url: string, body: FormData) {
    const response = await fetch(`${API_BASE_URL}/${url}`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${getToken()}`
      },
      body: body
    })
    await checkForValidationErrors(response)
    return await response.json() as T
  }, [])
  const patch = useCallback(async function<T>(url: string, body: object) {
    const response = await fetch(`${API_BASE_URL}/${url}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${getToken()}`
      },
      body: JSON.stringify(body)
    })
    await checkForValidationErrors(response)
    return await response.json() as {data: T}
  }, [])
  const del = useCallback(async function<T>(url: string) {
    const response = await fetch(`${API_BASE_URL}/${url}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${getToken()}`
      }
    })
    await checkForValidationErrors(response)
    return await response.json() as {data: T}
  }, [])

  return {
    getTasks: useCallback(async (date: Date) => {
      const json = await get<object[]>(`tasks?date=${Math.floor(date.getTime() / 1000)}`)
      return json.data.map(Task.fromJson)
    }, [get]),
    getWorkorders: useCallback(async (date: Date) => {
      const json = await get<object[]>(`workorders?date=${Math.floor(date.getTime() / 1000)}`)
      return json.data.map(Workorder.fromJson)
    }, [get]),
    getEmployees: useCallback(async () => {
      const json = await get<object[]>('employees')
      return json.data.map(Employee.fromJson)
    }, [get]),
    addEmployee: useCallback(async (employee: Pick<Employee, "firstName" | "lastName" | "email">) => {
      await post<void>(`employees`, employee.email === null ? {
        first_name: employee.firstName,
        last_name: employee.lastName,
      } : {
        first_name: employee.firstName,
        last_name: employee.lastName,
        email: employee.email,
      })
    }, [post]),
    getEmployeeWorkingDays: useCallback(async (employee: Employee) => {
      const json = await get<object>(`employees/${employee.id}/work_days`)
      return EmployeeWorkDays.fromJson(json.data)
    }, [get]),
    getEmployeeWorkingDaysOverride: useCallback(async (employee: Employee) => {
      const json = await get<object>(`employees/${employee.id}/work_days/override`)
      return EmployeeWorkDays.fromJson(json.data)
    }, [get]),
    getEmployeeWorkorders: useCallback(async (employee: Employee) => {
      const json = await get<object[]>(`employees/${employee.id}/workorders`)
      return json.data.map(Workorder.fromJson)
    }, [get]),
    updateEmployeeWorkingDays: useCallback(async (employee: Employee, days: EmployeeWorkDays) => {
      await post<void>(`employees/${employee.id}/work_days`, {
        even_mon: days.evenMonday,
        even_tue: days.evenTuesday,
        even_wed: days.evenWednesday,
        even_thu: days.evenThursday,
        even_fri: days.evenFriday,
        even_sat: days.evenSaturday,
        even_sun: days.evenSunday,
        odd_mon: days.oddMonday,
        odd_tue: days.oddTuesday,
        odd_wed: days.oddWednesday,
        odd_thu: days.oddThursday,
        odd_fri: days.oddFriday,
        odd_sat: days.oddSaturday,
        odd_sun: days.oddSunday,
        start_at: Math.floor(new Date().getTime() / 1000),
      })
    }, [post]),
    updateEmployeeWorkingDaysOverride: useCallback(async (employee: Employee, days: EmployeeWorkDays, startAt?: Date, endedAt?: Date) => {
      await post<void>(`employees/${employee.id}/work_days/override/${days.id}`, {
        even_mon: days.evenMonday,
        even_tue: days.evenTuesday,
        even_wed: days.evenWednesday,
        even_thu: days.evenThursday,
        even_fri: days.evenFriday,
        even_sat: days.evenSaturday,
        even_sun: days.evenSunday,
        odd_mon: days.oddMonday,
        odd_tue: days.oddTuesday,
        odd_wed: days.oddWednesday,
        odd_thu: days.oddThursday,
        odd_fri: days.oddFriday,
        odd_sat: days.oddSaturday,
        odd_sun: days.oddSunday,
        ...(startAt ? {start_at: Math.floor(startAt.getTime() / 1000)} : {}),
        ...(endedAt ? {end_at: Math.floor(endedAt.getTime() / 1000)} : {}),
      })
    }, [post]),
    addEmployeeWorkDaysOverride: useCallback(async (employee: Employee,  startAt: Date, endAt: Date) => {
      await post<void>(`employees/${employee.id}/work_days/override`, {
        start_at: Math.floor(startAt.getTime() / 1000),
        end_at: Math.floor(endAt.getTime() / 1000),
      })
    }, [post]),
    deleteWorkDaysOverride: useCallback(async (employee: Employee, workDays: string) => {
      await del<void>(`employees/${employee.id}/work_days/override/${workDays}`)
    }, [del]),
    updateEmployee: useCallback(async (employee: Pick<Employee, "id" | "firstName" | "lastName" | "enabled" | "email">, archiveDate?: Date) => {
      await post<void>(`employees/${employee.id}`, {
        enabled: employee.enabled,
        first_name: employee.firstName,
        last_name: employee.lastName,
        ...(employee.email ? {email: employee.email} : {}),
        ...(archiveDate ? {archive_date: archiveDate} : {})
      })
    }, [post]),
    updateEmployeePassword: useCallback(async (employee: Pick<Employee, "id">, password: string) => {
      await post<void>(`employees/${employee.id}/password`, {
        password: password,
      })
    }, [post]),
    saveEmployeeMetadata: useCallback(async (employeeId: string, color?: string | null, bsn?: string | null, contractStartDate?: Date | null, contractEndDate?: Date | null, hasTravelCompensation?: boolean, hasAllInSalary?: boolean, includeInHoursExport?: boolean, employeeType?: EmployeeType, shadowEmployee?: boolean) => {
      await post<void>(`employees/${employeeId}/metadata`, {
        ...(color !== undefined ? {color: color} : {}),
        ...(bsn !== undefined ? {bsn: bsn} : {}),
        ...((contractStartDate !== undefined && contractStartDate !== null) ? {contract_start_date: moment(contractStartDate).format('YYYY-MM-DD')} : {}),
        ...((contractEndDate !== undefined && contractEndDate !== null) ? {contract_end_date: moment(contractEndDate).format('YYYY-MM-DD')} : {}),
        ...(hasTravelCompensation !== undefined ? {has_travel_compensation: hasTravelCompensation} : {}),
        ...((hasAllInSalary !== undefined) ? {has_all_in_salary: hasAllInSalary} : {}),
        ...((includeInHoursExport !== undefined) ? {include_in_hours_export: includeInHoursExport} : {}),
        ...((employeeType !== undefined) ? {employee_type: employeeType} : {}),
        ...((shadowEmployee !== undefined) ? {shadow_employee: shadowEmployee} : {}),
      })
    }, [post]),
    getTeamAvailability: useCallback(async (date: Date): Promise<TeamAvailability> => {
      const json = await get<TeamAvailability>(`planning/team_availability?date=${Math.floor(date.getTime() / 1000)}`)
      return TeamAvailability.fromJson(json.data)
    }, [get]),
    getPlanningAbsence: useCallback(async (date: Date) => {
      const json = await get<{
        [day: string]: [] | { [employeeId: string]: { employee_uuid: string, reason: string } }
      }>(`planning/absence?date=${Math.floor(date.getTime() / 1000)}`)
      return PlanningAbsence.fromJsonMap(json.data)
    }, [get]),
    getTeams: useCallback(async (date: Date): Promise<Team[]> => {
      const json = await get<object[]>(`teams?date=${Math.floor(date.getTime() / 1000)}`)
      return json.data.map(Team.fromJson)
    }, [get]),
    addTeam: useCallback(async (team: Pick<Team, "name">, isTemp: boolean, date?: Date) => {
      if (isTemp && date === undefined) {
        date = new Date()
      }
      await post<void>(`teams`, {
        name: team.name,
        temporary: isTemp ? 1 : 0,
        date: moment(date).format('YYYY-MM-DD'),
      })
    }, [post]),
    editTeam: useCallback(async (team: Pick<Team, "id" | "name" | "category">, capacity?: number) => {
      await post<void>(`teams/${team.id}`, {
        name: team.name,
        category: team.category,
        capacity: capacity ?? null,
      })
    }, [post]),
    deleteTeam: useCallback(async (team: Team, date: Date) => {
      await post<void>(`teams/${team.id}`, {
        end_at: moment(date).format('YYYY-MM-DD'),
      })
    }, [del]),
    joinTeam: useCallback(async (team: Team, employee: Employee, date: Date, isTemporary?: boolean, reason?: string, endDate?: Date, isPaid?: boolean) => {
      const temporary = isTemporary ?? false
      const joinReason = reason ?? null
      await post<void>(`teams/${team.id}/members`, {
        employee_id: employee.id,
        from_date: Math.floor(date.getTime() / 1000),
        ...(endDate ? {end_date: Math.floor(endDate.getTime() / 1000)} : {}),
        temporary: temporary ? 1 : 0,
        reason: joinReason,
        paid: isPaid,
      })
    }, [post]),
    updateTeamsOrder: useCallback(async (teams: string[]) => {
      await post<void>(`teams/order`, {
        teams: teams,
      })
    }, [post]),
    deleteTemporaryTeam: useCallback(async (team: string) => {
      await del<void>(`teams/${team}/delete`)
    }, [del]),
    endTeamMembership: useCallback(async (teamId: string, membershipId: string, endDate: Date) => {
      await post<void>(`teams/${teamId}/members/${membershipId}/end`, {
        end_at: moment(endDate).format('YYYY-MM-DD'),
      })
    }, [post]),
    cancelPlannedChange: useCallback(async (teamId: string, membershipId: string) => {
      await del<void>(`teams/${teamId}/members/${membershipId}`)
    }, [del]),
    addTask: useCallback(async (projectId: string, description: string, type?: string, amount?: number, thickness?: number, note?: string, isImportant?: boolean) => {
      const json = await post<object[]>(`projects/${projectId}/tasks`, {
        task_name: description,
        ...(type !== undefined ? {quantity_type: type} : {}),
        ...(amount !== undefined ? {quantity_amount: amount} : {}),
        ...(thickness !== undefined ? {quantity_thickness: thickness} : {}),
        ...(note !== undefined ? {note: note} : {}),
        ...(isImportant !== undefined ? {is_workorder_remark_important: isImportant} : {})
      })
      return Task.fromJson(json.data[0])
    }, [post]),
    addTasks: useCallback(async (projectId: string, descriptions: string[], type?: string, amount?: number, thickness?: number, note?: string, isImportant?: boolean) => {
      const json = await post<object[]>(`projects/${projectId}/tasks`, {
        task_names: descriptions,
        ...(type !== undefined ? {quantity_type: type} : {}),
        ...(amount !== undefined ? {quantity_amount: amount} : {}),
        ...(thickness !== undefined ? {quantity_thickness: thickness} : {}),
        ...(note !== undefined? {note: note} : {}),
        ...(isImportant !== undefined ? {is_workorder_remark_important: isImportant} : {})
      })
      return json.data.map(t => Task.fromJson(t))
    }, [post]),
    planTask: useCallback(async (taskId: string, teamId: string|null, startAt: Date, durationMinutes: number) => {
      await post<object[]>(`tasks/${taskId}`, {
        start_at: moment(startAt).format(),
        duration: durationMinutes,
        team_uuid: teamId,
      })
    }, [post]),
    unPlanTask: useCallback(async (taskId: string) => {
      await post<object[]>(`tasks/${taskId}`, {
        start_at: null
      })
    }, [post]),
    taskToBuffer: useCallback(async (taskId: string, date: Date) => {
      await post<object[]>(`tasks/${taskId}`, {
        start_at: moment(date).format(),
      })
    }, [post]),
    batchPlanTask: useCallback(async (taskId: string, dates: Date[]) => {
      const json = await post<{ failed: {[date: string]: string} }>(`tasks/${taskId}/batch`, {
        dates: dates.map(d => moment(d).format('Y-MM-DD')),
      })
      return json.data.failed
    }, [post]),
    updateTaskColor: useCallback(async (taskId: string, color: string) => {
      await post<object[]>(`tasks/${taskId}`, {
        color,
      })
    }, [post]),
    updateTaskDescriptionAndQuantity: useCallback(async (taskId: string, description: string, quantityType: string, quantityAmount: number, quantityThickness: number, note: string, isImportant: boolean) => {
      await post<object[]>(`tasks/${taskId}`, {
        task_name: description,
        quantity_type: quantityType,
        quantity_amount: quantityAmount,
        quantity_thickness: quantityThickness,
        workorder_remark: note,
        is_workorder_remark_important: isImportant,
      })
    }, [post]),
    updateTaskInvoicedState: useCallback(async (taskId: string, isInvoiced: boolean) => {
      await post<object[]>(`tasks/${taskId}`, {
        is_invoiced: isInvoiced,
      })
    }, [post]),
    updateTaskVoidedState: useCallback(async (taskId: string, isVoided: boolean) => {
      await post<object[]>(`tasks/${taskId}`, {
        is_voided: isVoided,
      })
    }, [post]),
    updateTaskStatus: useCallback(async (taskId: string, status: TaskStatus) => {
      await post<object[]>(`tasks/${taskId}`, {
        status: status,
      })
    }, [post]),
    updateTaskMetadata: useCallback(async (taskId: string, fields: Partial<{workorderRemark: string|null, projectRemark: string|null, todos: {[text: string]: boolean}, is_project_remark_important: boolean}>) => {
      await post<object[]>(`tasks/${taskId}`, {
        ...(fields.workorderRemark !== undefined ? {workorder_remark: fields.workorderRemark} : {}),
        ...(fields.projectRemark !== undefined ? {project_remark: fields.projectRemark} : {}),
        ...(fields.todos !== undefined ? {todos: fields.todos} : {}),
        ...(fields.is_project_remark_important !== undefined ? {is_project_remark_important: fields.is_project_remark_important} : {}),
      })
    }, [post]),
    deleteTask: useCallback(async (task: Task) => {
      await del<void>(`tasks/${task.id}`)
    }, [del]),
    getGlobalHolidays: useCallback(async (date: Date): Promise<Holiday[]> => {
      const json = await get<object[]>(`planning/global_holidays?date=${Math.floor(date.getTime()/1000)}&include_unpublished=1`)
      return json.data.map(Holiday.fromJson)
    }, [get]),
    addGlobalHoliday: useCallback(async (dates: Date[], description: string, type: string) => {
      await post<void>(`planning/global_holidays`, {
        dates: dates.map(d => moment(d).format('Y-MM-DD')),
        name: description,
        type: type,
      })
    }, [post]),
    deleteGlobalHoliday: useCallback(async (holiday: Holiday) => {
      await del<void>(`planning/global_holidays/${holiday.id}`)
    }, [del]),
    getGlobalHolidayYears: useCallback(async (): Promise<GlobalHolidayYear[]> => {
      const json = await get<object[]>(`planning/global_holiday_years`)
      return json.data.map(GlobalHolidayYear.fromJson)
    }, [get]),
    updateGlobalHolidayYearStatus: useCallback(async (year: number, status: 'draft'|'published'): Promise<GlobalHolidayYear> => {
      const json = await patch<object>(`planning/global_holiday_years/${year}`, {status})
      return GlobalHolidayYear.fromJson(json.data)
    }, [patch]),
    addQuantityType: useCallback(async (name: string, enabled: boolean, defaultColorId: string|null, descriptionTemplate: string[]|null, hideArea: boolean, shouldMakeWorkorder: boolean, countsForManDaySheet: boolean) => {
      const template = descriptionTemplate?.join('') === '' ? null : descriptionTemplate
      await post<void>(`quantities/types`, {
        name: name,
        enabled: enabled,
        default_color: defaultColorId,
        description_template: template,
        hide_area: hideArea,
        should_generate_workorder: shouldMakeWorkorder,
        counts_for_man_day_sheet: countsForManDaySheet,
      })
    }, [post]),
    editQuantityType: useCallback(async (id: string, name: string, enabled: boolean, defaultColorId: string|null, descriptionTemplate: string[]|null, hideArea: boolean, shouldMakeWorkorder: boolean, countsForManDaySheet: boolean) => {
      const template = descriptionTemplate?.join('') === '' ? null : descriptionTemplate
      await post<void>(`quantities/types/${id}`, {
        name: name,
        enabled: enabled,
        ...(defaultColorId ? {default_color: defaultColorId} : {}),
        description_template: template,
        hide_area: hideArea,
        should_generate_workorder: shouldMakeWorkorder,
        counts_for_man_day_sheet: countsForManDaySheet,
      })
    }, [post]),
    addQuantityTypeTemplate: useCallback(async (typeId: string, taskTypeTemplate: Pick<QuantityTypeTemplate, "description"|"order"|"shouldGenerateWorkorder">) => {
      await post<void>(`quantities/types/${typeId}/templates`, {
        description: taskTypeTemplate.description,
        order: taskTypeTemplate.order,
        should_generate_workorder: taskTypeTemplate.shouldGenerateWorkorder,
        question_set: {questions: []}
      })
    }, [post]),
    editQuantityTypeTemplate: useCallback(async (typeId: string, taskTypeTemplate: QuantityTypeTemplate) => {
      await put<void>(`quantities/types/${typeId}/templates/${taskTypeTemplate.id}`, {
        description: taskTypeTemplate.description,
        order: taskTypeTemplate.order,
        should_generate_workorder: taskTypeTemplate.shouldGenerateWorkorder,
        question_set: {questions: taskTypeTemplate.questions.map((question) => ({
            uuid: question.id,
            order: question.order,
            type: question.type,
            question: question.question,
            picture_requirements: question.pictureRequirements,
            should_export: question.should_export,
            ...(question.exportName ? { export_name: question.exportName } : {}),
            ...(question.type === 'multi' ? {
              options: question.options,
              include_other_option: question.includeOtherOption
            } : {}),
            ...(question.type === 'value' ? {
              min: question.min,
              max: question.max,
              export_multiplier: question.export_multiplier,
              export_decimals: question.export_decimals,
              export_unit: question.export_unit,
            } : {})
          }))}
      })
    }, [put]),
    editQuantityTypeTemplates: useCallback(async (typeId: string, templates: Pick<QuantityTypeTemplate,"id"|"order"|"description"|"shouldGenerateWorkorder">[]) => {
      await post<void>(`quantities/types/${typeId}/templates/bulkedit`, {
        templates: templates.map(t => ({
          id: t.id,
          order: t.order,
          description: t.description,
          should_generate_workorder: t.shouldGenerateWorkorder,
        })),
      })
    }, [post]),
    deleteQuantityTypeTemplate: useCallback(async (typeId: string, templateId: string) => {
      await del<void>(`quantities/types/${typeId}/templates/${templateId}`)
    }, [del]),
    getSuppliers: useCallback(async () => {
      const json = await get<object[]>(`suppliers`)
      return json.data.map(Supplier.fromJson)
    }, [get]),
    getRelations: useCallback(async () => {
      const json = await get<object[]>(`relations`)
      return json.data.map(Relation.fromJson)
    }, [get]),
    getVatRates: useCallback(async () => {
      const json = await get<object[]>(`vat-rates`)
      return json.data.map(VatRate.fromJson)
    }, [get]),
    getLedgers: useCallback(async () => {
      const json = await get<object[]>(`ledgers`)
      return json.data.map(Ledger.fromJson)
    }, [get]),
    addSupplier: useCallback(async (supplier: Pick<Supplier, "name">) => {
      await post<void>(`suppliers`, {
        name: supplier.name,
      })
    }, [post]),
    editSupplier: useCallback(async (supplier: Pick<Supplier, "id"|"name">) => {
      await post<void>(`suppliers/${supplier.id}`, {
        name: supplier.name,
      })
    }, [post]),
    editRoles: useCallback(async (employee: string, isOffice: boolean, isManager: boolean) => {
      await post<void>(`employees/${employee}/roles`, {
        office: isOffice,
        manager: isManager,
      })
    }, [post]),
    addTaskSupplier: useCallback(async (taskId: string, relationId: string, description: string|null, status: "green"|"orange"|"red", order: string|null) => {
      await post<void>(`tasks/${taskId}/suppliers`, {
        relation_id: relationId,
        status: status,
        ...(description !== null ? {description} : {}),
        ...(order !== null ? {order_id: order} : {}),
      })
    }, [post]),
    updateTaskSupplierStatus: useCallback(async (taskId: string, supplierId: string, status: "green"|"orange"|"red") => {
      await post<void>(`tasks/${taskId}/suppliers/${supplierId}`, {
        status: status,
        is_complete: status === "green",
      })
    }, [post]),
    updateTaskSupplier: useCallback(async (taskId: string, supplierId: string, status: string, description: string|null, order: string|null) => {
      await post<void>(`tasks/${taskId}/suppliers/${supplierId}`, {
        description: description,
        order_id: order,
        status: status,
      })
    }, [post]),
    deleteTaskSupplier: useCallback(async (taskId: string, supplierId: string) => {
      await del<void>(`tasks/${taskId}/suppliers/${supplierId}`)
    }, [del]),

    // Quantities
    getQuantityTypes: useCallback(async () => {
      const json = await get<{name: string, gradient: number|null, intercept: number|null}[]>('quantities/types')
      return json.data.map(QuantityType.fromJson)
    }, [get]),

    // Colors
    getColors: useCallback(async () => {
      const json = await get<{id: string,name: string,enabled: boolean,color: string,colorName: string}[]>('colors')
      return json.data
    }, [get]),
    addColor: useCallback(async (name: string, enabled: boolean, color: string) => {
      const json = await post<{id: string, name: string, enabled: boolean, color: string, colorName: string}[]>('colors', {
        name: name,
        enabled: enabled,
        color: color
      })
      return json.data
    }, [post]),
    updateColor: useCallback(async (id: string, name: string, enabled: boolean, color: string) => {
      const json = await post<{id: string, name: string, enabled: boolean, color: string, colorName: string}[]>(`colors/${id}`, {
        name: name,
        enabled: enabled,
        color: color
      })
      return json.data
    }, [post]),

    // Orders
    getOrders: useCallback(async () => {
      const json = await get<{id: string,date: string, text: string, project_id: number, amount: number, quantity: number, quantity_unit: string, is_done: boolean, remark: string}[]>('orders')
      return json.data.map(j => Order.fromJson(j))
    }, [get]),
    addOrder: useCallback(async (projectId: number, date: Date, text: string, amount?: number, quantity?: number, quantityUnit?: string, remark?: string) => {
      const json = await post<{id: string,date: string, text: string, project_id: number, amount: number, quantity: number, quantity_unit: string, is_done: boolean, remark: string}>('orders', filterUndefinedValues({
        project_id: projectId,
        date: moment(date).format('YYYY-MM-DD'),
        text: text,
        amount: amount,
        quantity: quantity,
        quantity_unit: quantityUnit,
        remark: remark,
      }))
      return Order.fromJson(json.data)
    }, [post]),
    updateOrder: useCallback(async (id: string, projectId?: number, date?: Date, text?: string, amount?: number, quantity?: number, quantityUnit?: string, remark?: string, isDone?: boolean) => {
      const json = await post<{id: string,date: string, text: string, project_id: number, amount: number, quantity: number, quantity_unit: string, is_done: boolean, remark: string}>(`orders/${id}`, filterUndefinedValues({
        project_id: projectId,
        date: date === undefined ? undefined : moment(date).format('YYYY-MM-DD'),
        text: text,
        amount: amount,
        quantity: quantity,
        quantity_unit: quantityUnit,
        remark: remark,
        is_done: isDone,
      }))
      return Order.fromJson(json.data)
    }, [post]),
    deleteOrder: useCallback(async (id: string) => {
      await del(`orders/${id}`)
    }, [post]),

    getNotes: useCallback(async (date: Date) => {
      const json = await get<object[]>(`notes?date=${Math.floor(date.getTime()/1000)}`)
      return json.data.map(Note.fromJson)
    }, [get]),
    editNote: useCallback(async (date: Date, text: string) => {
      const json = await post<object>('notes', {
        date: moment(date).format('YYYY-MM-DD'),
        text: text,
      })
      return Note.fromJson(json.data)
    }, [post]),
    sendWorkorders: useCallback(async (taskIds: string[], note?: string, isImportant?: boolean, sendNotifications?: boolean) => {
      await post<void>('workorders', {
        task_ids: taskIds,
        note: note,
        isImportant: isImportant,
        sendNotifications: sendNotifications,
      })
    }, [post]),
    archiveWorkOrder: useCallback(async (workorderId: string) => {
      await post<void>(`workorders/${workorderId}/archive`, {
        archived_at: moment(Date.now()).format('YYYY-MM-DD'),
      })
    }, [post]),
    unarchiveWorkOrder: useCallback(async (workorderId: string) => {
      await post<void>(`workorders/${workorderId}/archive`, {
        archived_at: null,
      })
    }, [post]),
    deleteWorkorders: useCallback(async (workorders: string[]) => {
      await del<void>(`workorders?ids=${workorders.join(',')}`)
    }, [del]),
    downloadHourReport: useCallback(async (year: number, period: number|null) => {
      const response = await postRaw<object[]>('export/hours', {year, period})
      if (response.status !== 200) {
        console.error(response.status)
        console.error(await response.text())
        throw new Error('Failed to download report')
      }
      return await response.blob()
    }, [postRaw]),
    downloadWorkorderPdf: useCallback(async (workorderId: string) => {
      const response = await postRaw<object[]>(`workorders/${workorderId}/export`, {})
      if (response.status !== 200) {
        console.error(response.status)
        console.error(await response.text())
        throw new Error('Failed to download report')
      }
      return await response.blob()
    }, [postRaw]),
    downloadBatchWorkorderPdf: useCallback(async (workorderIds: string[]) => {
      const response = await postRaw<object[]>(`workorders/batch_export`, {
        workorderIds: workorderIds,
      })
      if (response.status !== 200) {
        console.error(response.status)
        console.error(await response.text())
        throw new Error('Failed to download report')
      }
      return await response.blob()
    }, [postRaw]),
    getDailyRemarks: useCallback(async (date: Date) => {
      const json = await get<object[]>(`daily_remarks?date=${Math.floor(date.getTime()/1000)}`)
      return json.data.map(DailyRemark.fromJson)
    }, [get]),
    editDailyRemark: useCallback(async (date: Date, teamId: string, remark: string) => {
      const json = await post<object>('daily_remarks', {
        date: moment(date).format('YYYY-MM-DD'),
        team_uuid: teamId,
        remark: remark,
      })
      return DailyRemark.fromJson(json.data)
    }, [post]),
    updateEmployeeEmergencyDetails: useCallback(async (employeeId: string, emergencySelfName: string|null, emergencySelfStreetname: string|null, emergencySelfHouseNumber: string|null, emergencySelfHouseNumberAddition: string|null, emergencySelfZipcode: string|null, emergencySelfCity: string|null, emergencySelfDateOfBirth: Date|null, emergencySelfPhone: string|null, contactName?: string|null, contactPhone?: string|null, contactStreetname?: string|null, contactHouseNumber?: string|null, contactHouseNumberAddition?: string|null, contactZipcode?: string|null, contactCity?: string|null, gpName?: string|null, gpPhone?: string|null, gpStreetname?: string|null, gpHouseNumber?: string|null, gpHouseNumberAddition?: string|null, gpZipcode?: string|null, gpCity?: string|null, gpAllergies?: string|null, gpNote?: string|null) => {
      await put<void>(`employees/${employeeId}/emergency_contacts`, {
        ...(emergencySelfName !== '' ? {emergency_self_name: emergencySelfName} : {}),
        ...(emergencySelfStreetname !== '' ? {emergency_self_streetname: emergencySelfStreetname} : {}),
        ...(emergencySelfHouseNumber !== '' ? {emergency_self_house_number: emergencySelfHouseNumber} : {}),
        ...(emergencySelfHouseNumberAddition !== '' ? {emergency_self_house_number_addition: emergencySelfHouseNumberAddition} : {}),
        ...(emergencySelfZipcode !== '' ? {emergency_self_zipcode: emergencySelfZipcode} : {}),
        ...(emergencySelfCity !== '' ? {emergency_self_city: emergencySelfCity} : {}),
        ...(emergencySelfDateOfBirth !== null ? {emergency_self_date_of_birth: moment(emergencySelfDateOfBirth).format('YYYY-MM-DD')} : {}),
        ...(emergencySelfPhone !== '' ? {emergency_self_phone: emergencySelfPhone} : {}),
        ...(contactName !== '' ? {emergency_contact_name: contactName} : {}),
        ...(contactPhone !== '' ? {emergency_contact_phone: contactPhone} : {}),
        ...(contactStreetname !== '' ? {emergency_contact_streetname: contactStreetname} : {}),
        ...(contactHouseNumber !== '' ? {emergency_contact_house_number: contactHouseNumber} : {}),
        ...(contactHouseNumberAddition !== '' ? {emergency_contact_house_number_addition: contactHouseNumberAddition} : {}),
        ...(contactZipcode !== '' ? {emergency_contact_zipcode: contactZipcode} : {}),
        ...(contactCity !== '' ? {emergency_contact_city: contactCity} : {}),
        ...(gpName !== '' ? {emergency_gp_name: gpName} : {}),
        ...(gpPhone !== '' ? {emergency_gp_phone: gpPhone} : {}),
        ...(gpStreetname !== '' ? {emergency_gp_streetname: gpStreetname} : {}),
        ...(gpHouseNumber !== '' ? {emergency_gp_house_number: gpHouseNumber} : {}),
        ...(gpHouseNumberAddition !== '' ? {emergency_gp_house_number_addition: gpHouseNumberAddition} : {}),
        ...(gpZipcode !== '' ? {emergency_gp_zipcode: gpZipcode} : {}),
        ...(gpCity !== '' ? {emergency_gp_city: gpCity} : {}),
        ...(gpAllergies !== '' ? {emergency_gp_allergies: gpAllergies} : {}),
        ...(gpNote !== '' ? {emergency_gp_note: gpNote} : {}),
      })
    }, []),
    addMediaToWorkorder: useCallback( async (taskId: string, file: File) => {
      const formData = new FormData();
      formData.append('file', file, file.name)
      await postForm<object>(`tasks/${taskId}/media`, formData)
    }, [postForm]),
    deleteMediaFromWorkorder: useCallback( async (taskId: string, attachmentId: string) => {
      await del<void>(`tasks/${taskId}/media/${attachmentId}`)
    }, [del]),
    addProject: useCallback( async (relations: string[]|null, description: string|null, projectMemo: string|null, projectLocation: string|null,
                                    customerRelation: string|null, customerContact: string|null, deliveryName: string|null, deliveryRelation: string|null,
                                    deliveryContact: string|null, deliveryAddress: string|null, deliveryCity: string|null, deliveryPostalcode: string|null, deliveryPhone: string|null, deliveryEmail: string|null) => {
      const response = await post<void>(`projects`, {
        ...(Array.isArray(relations) && relations?.length > 0 ? {relations: relations} : {}),
        ...(description !== '' ? {description: description} : {}),
        ...(projectMemo !== '' ? {projectMemo: projectMemo} : {}),
        ...(projectLocation !== '' ? {projectLocation: projectLocation} : {}),
        ...(customerRelation !== '' ? {customerRelation: customerRelation} : {}),
        ...(customerContact !== '' ? {customerContact: customerContact} : {}),
        ...(deliveryName !== '' ? {deliveryName: deliveryName} : {}),
        ...(deliveryRelation !== '' ? {deliveryRelation: deliveryRelation} : {}),
        ...(deliveryContact !== '' ? {deliveryContact: deliveryContact} : {}),
        ...(deliveryAddress !== '' ? {deliveryAddress: deliveryAddress} : {}),
        ...(deliveryCity !== '' ? {deliveryCity: deliveryCity} : {}),
        ...(deliveryPostalcode !== '' ? {deliveryPostcode: deliveryPostalcode} : {}),
        ...(deliveryPhone !== '' ? {deliveryPhone: deliveryPhone} : {}),
        ...(deliveryEmail !== '' ? {deliveryEmail: deliveryEmail} : {}),
      })
    }, [post]),
    getTaskTemplate: useCallback(async (templateId: string) => {
      const json = await get<object[]>(`quantities/types/templates/${templateId}`)
      return QuantityTypeTemplate.fromJson(json.data)
    }, [get]),
    getTaskTemplates: useCallback(async () => {
      const json = await get<object[]>(`quantities/types/templates`)
      return json.data.map((item) => QuantityTypeTemplate.fromJson(item))
    }, [get]),
  }
}

export type ErrorBag = { [key: string]: string[] }
export class ValidationError extends Error {
  constructor(public readonly message: string, public readonly errors: { [key: string]: string[] }) {
    super(message);
  }
}
